import { Divider } from "antd";
import React, { useMemo } from "react";
import LoadingBox from "../../../../../components/LoadingBox";
import Density from "../../../../../components/Operation/Density2";
import { datetime } from "../../../../../util/library/datetime";
import {
    DELIVERY_TYPE,
    deliveryType,
    KeyOfStep,
    SHORT_DATETIME_FORMAT
} from "../../../../../util/varibles/constants";
import { convertStepName, durationTxt } from "../../../../../util/varibles/global";
import {
    IActivityLog,
    IHarvest,
    IHarvest2,
    IOperation2,
    IStorage,
    ISubStepActivityLog,
    IUnit
} from "../../../../../util/varibles/interface";
import InfoFish from "../_components/InfoFish";
import { useEditOp } from "../EditOperationContext";
import styles from "./style.module.scss";

interface IStepProps {
    data: IActivityLog
    op: IOperation2<IHarvest2>
    vesselTanks: IStorage[] | IHarvest['tanks']
}

const NormalStep: React.FC<IStepProps> = ({ data }) => {
    const { est_start_time, real_start_time } = data;
    return <div className={styles.step}>
        <div data-lev='est-time'>{datetime(est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
        {real_start_time && <span>{datetime(real_start_time).format(SHORT_DATETIME_FORMAT)}</span>}
        <div className='text-[18px]'>{convertStepName([data.key])}</div>
    </div>;
};

const Harvesting: React.FC<IStepProps> = ({ data, op, vesselTanks }) => {
    const tanks = (op.operation.tanks || []).reduce((rs: { [id: string]: IHarvest['tanks'][0] }, item) => {
        rs[item.id] = item
        return rs
    }, {})
    const units = op.operation.sub_operations.reduce((rs: { [id: string]: IUnit }, item) => {
        rs[item.id] = item
        return rs
    }, {})

    const groupByUnit = (data.sub_steps || []).reduce((rs: {
        [id: string]: { [id: string]: ISubStepActivityLog }
    }, item) => {
        const { sub_id = '', tank_id } = item;
        rs[sub_id] = {
            ...rs[sub_id] || {},
            [tank_id]: item
        }
        return rs;
    }, {})

    return <div className={styles.step}>
        {Object.keys(groupByUnit).map(key => {
            const unit = groupByUnit[key] || {};
            const { unit_id } = units[key] || {};
            return <div key={key} className='flex gap-4 w-full'>
                {vesselTanks.map(item => {
                    const {
                        est_start_time = 0,
                        real_start_time,
                        est_finish_time,
                        real_finish_time
                    } = unit[item.id || ''] || {};
                    if (!est_start_time)
                        return <div key={item.id} className={styles.tankStep} />

                    return <div key={item.id} className={styles.tankStep}>
                        <div className='font-[600] mb-1'>{unit_id}</div>
                        <div className='flex flex-col'>
                            <div data-lev='est-time'>{datetime(est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                            {real_start_time && <div>{datetime(real_start_time).format(SHORT_DATETIME_FORMAT)}</div>}
                        </div>
                        <div className='flex flex-col items-end' data-lev='job'>
                            <div className='text-[18px] leading-[20px]'>Loading</div>
                            <div className='flex'>(<InfoFish data={unit[item.id || ''] || {}} />)</div>
                            <div className={[styles.arrow, 'arrow-down'].join(' ')}></div>
                        </div>
                        <div className='flex flex-col'>
                            <div data-lev='est-time'>{datetime(est_finish_time).format(SHORT_DATETIME_FORMAT)}</div>
                            {real_finish_time && <div>{datetime(real_finish_time).format(SHORT_DATETIME_FORMAT)}</div>}
                        </div>
                    </div>
                })}
            </div>
        })}
        <div className='flex gap-4 w-full mt-2'>
            {vesselTanks.map(item => {
                const { density = 0, max_density = 0 } = tanks[item.id || ''] || {};
                return <div className='tank-op' key={item.id}>
                    <div className='bg-[var(--bgSidebar1)] px-[10px] flex items-center'>
                        <InfoFish data={tanks[item.id || ''] || {}} />
                    </div>
                    <div className='flex justify-between gap-2 px-[10px]'>
                        <div>{item.name}</div>
                        <Density density={density} max_density={max_density} />
                    </div>
                </div>
            })}
        </div>
    </div>;
};

const Discharging: React.FC<IStepProps> = ({ data, op, vesselTanks }) => {
    const { sub_operations = [] } = op.operation;

    const discharging = useMemo(() => sub_operations.reduce((rs: {
        [id: string]: { name: string, fish_amount?: number, total_weight?: number }
    }, { delivery_types = [], id }) => {
        delivery_types.forEach(({ tanks = [], id: deliveryTypeId, name: deliveryTypeName }) =>
            tanks.forEach(tank =>
                rs[[id, deliveryTypeId, tank.id].join('/')] = { ...tank, name: deliveryTypeName }))
        return rs;
    }, {}), [sub_operations]);

    const groupByTank = (data.sub_steps || []).reduce((rs: {
        [id: string]: ISubStepActivityLog[]
    }, item) => {
        const { tank_id } = item;
        rs[tank_id] = [...rs[tank_id] || [], item]
        return rs;
    }, {})

    return <div className={styles.step}>
        <div className='flex gap-4 w-full'>
            {vesselTanks.map(item => {
                const data = groupByTank[item.id || ''] || [];
                return <div className='flex flex-col gap-2 w-full' key={item.id}>
                    {data.map(sub => {
                        const {
                            est_start_time,
                            est_finish_time,
                            real_start_time,
                            real_finish_time,
                            delivery_type_id = '',
                            tank_id = ''
                        } = sub;
                        const key = [sub.sub_id, delivery_type_id, tank_id].join('/');
                        const { name = '' } = discharging[key] || {};
                        const suffix = name.indexOf(deliveryType[DELIVERY_TYPE.DIRECT].name) === -1 ? 'into' : '';
                        return <div key={sub.group_id} className={styles.tankStep}>
                            <div className='flex flex-col'>
                                <div data-lev='est-time'>{datetime(est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                                {real_start_time &&
                                    <div>{datetime(real_start_time).format(SHORT_DATETIME_FORMAT)}</div>}
                            </div>
                            <div className='flex flex-col items-end' data-lev='job'>
                                <div className='text-[18px] leading-[20px]'>Discharging {suffix} {name}</div>
                                <div className='flex text-[14px]'>
                                    {discharging[key] && <>(<InfoFish data={discharging[key]} />)</>}
                                </div>
                                <div className={[styles.arrow, 'arrow-down'].join(' ')}></div>
                            </div>
                            <div className='flex flex-col'>
                                <div data-lev='est-time'>{datetime(est_finish_time).format(SHORT_DATETIME_FORMAT)}</div>
                                {real_finish_time &&
                                    <div>{datetime(real_finish_time).format(SHORT_DATETIME_FORMAT)}</div>}
                            </div>
                        </div>
                    })}
                </div>
            })}
        </div>
    </div>;
};

const stepViews: { [id: string]: React.FC<IStepProps> } = {
    [KeyOfStep.HARVEST]: Harvesting,
    [KeyOfStep.DISCHARGE_FACTORY]: Discharging
}

interface IActivityLogProps {
    data: IOperation2<IHarvest2>
    vesselTanks?: IStorage[] | IHarvest['tanks']
}

export const ActivityLog: React.FC<IActivityLogProps> = ({ data, vesselTanks }) => {
    const { activity_log = [], operation, loading = false, } = data;
    const { operation_code, total_weight } = operation as IHarvest;
    const start = datetime(activity_log[0]?.est_start_time);
    const finish = datetime(activity_log[activity_log.length - 1]?.est_start_time);
    const isRoute = !operation.error_detail?.route;

    return <div className={styles.activityLog}>
        <div data-lev='loading' data-loading={loading}>
            {loading && <LoadingBox className='min-h-[30px]' loading={true} />}
        </div>
        {isRoute && <>
            <div className="text-[12px] font-[600] w-full z-[2]">
                <span>#{operation_code}</span>
                <Divider orientation="vertical" />
                <span className="uppercase">Total weight: {Math.round(total_weight / 1000000)} T</span>
                <Divider orientation="vertical" />
                <span>{durationTxt(finish.time - start.time)}</span>
            </div>
            <div className="flex flex-col w-full">
                {activity_log.map((item, i) => {
                    const { key } = item;
                    const { [key]: View = NormalStep } = stepViews;
                    return <View
                        key={key + i}
                        data={item}
                        op={data}
                        vesselTanks={vesselTanks || data.operation.tanks || []}
                    />
                })}
            </div>
        </>}
    </div>;
};

const ActivityLogs: React.FC = () => {
    const [ops] = useEditOp(state => state.harvests);
    const [vesselTanks = []] = useEditOp(state => state.vesselTanks);

    return <div className="flex flex-col gap-[16px] mb-[18px] px-[20px]">
        {ops.map((item) => <ActivityLog
            key={item?.operation?.id}
            data={item as IOperation2<IHarvest2>}
            vesselTanks={vesselTanks}
        />)}
    </div>;
};

export default ActivityLogs;
